import { DIENST_TYPE_LABELS } from '@streep/types'
import type { DienstType, PloegSoort, WerkbonType } from '@streep/types'
import { weekStringFromDate } from './iso-week'

/**
 * Pure helpers voor de Werkbon-Create-flow. Op basis van fs-pro
 * `WerkbonCreateModal.tsx` (auto-omschrijving) en handover §5.3.
 *
 * NOTE: `getRotatiePatronen` / `RotatiePatroon` / `PATRONEN_*` zijn verwijderd
 * (spec 07). De werkbon-create-flow gebruikt nu dag-gebaseerde shift-roster
 * presets uit `shift-roster.ts`.
 */

// ── Frequentie defaults per dienstType ──────────────────────────────────────

/**
 * Aantal weken tussen werkbon-iteraties. Voor ploegendienst altijd 1: er moet
 * elke werk-week één werkbon aangemaakt worden, en de ploeg rouleert per week
 * via `ploegRotatie` (Variant A — elke week wisselen). Bij hogere waarden
 * sloeg `generateRecurringDates` weken over en kreeg de medewerker gaten in
 * z'n planning bij multi-week ploegen-rotaties.
 */
export function defaultFrequentieWeken(_dienstType: DienstType): number {
  return 1
}

export function isPloegen(dienstType: DienstType): boolean {
  return dienstType !== 'DAGDIENST'
}

// ── Default dienst-tijden per ploeg ─────────────────────────────────────────

export type DienstTijdenSet = Record<PloegSoort, { start: string; eind: string }>

export const DEFAULT_DIENSTTIJDEN: DienstTijdenSet = {
  DAG: { start: '08:00', eind: '17:00' },
  VROEG: { start: '06:00', eind: '14:15' },
  LAAT: { start: '14:15', eind: '23:00' },
  NACHT: { start: '23:00', eind: '06:00' },
}

// ── UI labels (single source) ───────────────────────────────────────────────

// `DIENST_TYPE_LABELS` is centraal gedefinieerd in `@streep/types` (waarden
// `'Dagdienst'`, `'2-ploegendienst'`, …). We re-exporteren hier zodat
// bestaande imports uit `@streep/utils` blijven werken, maar er is nog maar
// één bron-of-truth voor de labels. Bij wijziging: pas `packages/types/src/
// werkbonnen.ts` aan, niet hier.
export { DIENST_TYPE_LABELS }

export const PLOEG_LABELS: Record<PloegSoort, string> = {
  DAG: 'Dag',
  VROEG: 'Ochtend',
  LAAT: 'Middag',
  NACHT: 'Nacht',
}

// ── Auto-omschrijving generatie ─────────────────────────────────────────────

interface GenereerOmschrijvingInput {
  klantNaam: string | null | undefined
  medewerkerNaam: string | null | undefined
  dienstType: DienstType
  /** Expliciet jaar; wint van `startWeek`. */
  jaar?: number
  /** Formaat `wk-jaar` (bv. `18-2026`). Wordt gebruikt om het jaar af te leiden als `jaar` ontbreekt. */
  startWeek?: string | null
}

/**
 * Genereert een leesbare werkbon-omschrijving in formaat (#10 punt 8):
 *   `${klantNaam} - wk ${week} - ${jaar} - ${voornaamMedewerker}`
 *
 * Bv. `Bato Plastics BV - wk 12 - 2026 - Mustafa`.
 *
 * - Joiner is ` - ` (spatie-streepje-spatie).
 * - Klantnaam blijft in originele case (`Bato Plastics BV`, geen lowercase).
 * - Voornaam van medewerker behoudt originele case (`Mustafa`, niet `mustafa`)
 *   en blijft het LAATSTE ` - `-segment, zodat `hernoemWerkbonNaamgeving` het
 *   naamdeel bij omplannen blijft vinden.
 * - Week + jaar worden uit `startWeek` (`wk-jaar`) afgeleid; expliciete `jaar`
 *   wint voor het jaartal (backwards-compat); ontbreekt de bron, dan de huidige
 *   ISO-week/jaar.
 * - De dienstsoort is bewust vervallen uit de omschrijving (#10).
 * - Ontbrekende klant/medewerker worden weggelaten — géén dubbele streepjes.
 *   Geen klant *en* geen medewerker → leeg.
 */
export function genereerWerkbonOmschrijving(input: GenereerOmschrijvingInput): string {
  const { klantNaam, medewerkerNaam, startWeek } = input
  const klantTrim = klantNaam?.trim() ?? ''
  const medewerkerTrim = medewerkerNaam?.trim() ?? ''
  if (!klantTrim && !medewerkerTrim) return ''

  // Week + jaar afleiden uit startWeek ("wk-jaar"); expliciete `jaar` wint voor
  // het jaartal. Ontbreekt de bron, val terug op de huidige ISO-week/jaar.
  let week: number | null = null
  let jaar: number | null = null
  if (startWeek && /^\d{1,2}-\d{4}$/.test(startWeek)) {
    const [wStr, jStr] = startWeek.split('-')
    const w = Number(wStr)
    const j = Number(jStr)
    if (Number.isFinite(w)) week = w
    if (Number.isFinite(j)) jaar = j
  }
  if (typeof input.jaar === 'number' && Number.isFinite(input.jaar)) {
    jaar = input.jaar
  }
  if (week === null || jaar === null) {
    const [hwStr, hjStr] = weekStringFromDate(new Date()).split('-')
    if (week === null) week = Number(hwStr)
    if (jaar === null) jaar = Number(hjStr)
  }

  const voornaam = medewerkerTrim.split(/\s+/)[0] ?? ''
  const parts: string[] = [klantTrim, `wk ${week}`, String(jaar), voornaam]
  return parts.filter((p) => p.length > 0).join(' - ')
}

// ── Reeks-naamgeving: per week-kind de eigen week in de auto-naam ───────────

export interface NaamgevingPerWeekInput {
  /** De meegestuurde omschrijving of referentie (body-waarde) van de create. */
  waarde: string | null | undefined
  klantNaam: string | null | undefined
  medewerkerNaam: string | null | undefined
}

/**
 * Reeks-create: elke week krijgt zijn eigen werkbon (1 werkbon = 1 week), maar
 * de auto-naamgeving (`${klant} - wk ${week} - ${jaar} - ${voornaam}`) werd één
 * keer gegenereerd met de startweek en vervolgens letterlijk naar elk week-kind
 * gekopieerd - waardoor een bon in week 32 "wk 27" in z'n naam droeg.
 *
 * Deze factory beslist per veld: is `waarde` exact de onaangepaste auto-string
 * voor deze klant/medewerker (met een willekeurige "wk N - JJJJ" erin - de week
 * zelf telt niet mee, want de modal kan met een andere week gegenereerd hebben
 * dan de effectieve startweek)? Dan levert de resolver per week-kind de
 * hergenereerde naam met diens eigen `weekNr`. Elke andere (= handmatig
 * getypte) waarde blijft letterlijk behouden voor de hele reeks.
 */
export function maakNaamgevingPerWeek(
  input: NaamgevingPerWeekInput,
): (weekNr: string | null | undefined) => string | null {
  const vast = input.waarde ?? null
  const letterlijk = () => vast
  if (!vast) return letterlijk

  // De generator negeert dienstType in de output (#10, dienstsoort vervallen);
  // we geven een vaste waarde mee puur voor de signatuur.
  const basis = {
    klantNaam: input.klantNaam,
    medewerkerNaam: input.medewerkerNaam,
    dienstType: 'DAGDIENST' as const,
  }

  // Sentinel-week: laat de generator zelf de exacte vorm bepalen (segmenten
  // vallen weg bij ontbrekende klant/naam) en generaliseer daarna alléén het
  // "wk N - JJJJ"-deel. '1-1111' komt in echte data nooit voor. NB: bewust
  // strenger dan hernoemWerkbonNaamgeving - de create-modal schrijft altijd de
  // VOORNAAM-variant, dus de volledige-naam-variant telt hier als handmatig.
  const sentinel = genereerWerkbonOmschrijving({ ...basis, startWeek: '1-1111' })
  const delen = sentinel.split('wk 1 - 1111')
  if (delen.length !== 2) return letterlijk

  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const autoVorm = new RegExp(`^${esc(delen[0]!)}wk \\d{1,2} - \\d{4}${esc(delen[1]!)}$`)
  if (!autoVorm.test(vast)) return letterlijk

  return (weekNr) => {
    if (!weekNr || !/^\d{1,2}-\d{4}$/.test(weekNr)) return vast
    const hergenereerd = genereerWerkbonOmschrijving({ ...basis, startWeek: weekNr })
    return hergenereerd || vast
  }
}

// ── Hernoemen bij omplannen naar een andere medewerker ──────────────────────

export interface HernoemWerkbonNaamgevingInput {
  huidigeOmschrijving: string | null | undefined
  huidigeReferentie: string | null | undefined
  klantNaam: string | null | undefined
  dienstType: DienstType
  /** Expliciet jaar; wint van `startWeek` (zelfde semantiek als genereerWerkbonOmschrijving). */
  jaar?: number
  /** Formaat `wk-jaar` (bv. `18-2026`), zoals `Werkbon.weekNr`. */
  startWeek?: string | null
  oudeMedewerkerNaam: string | null | undefined
  nieuweMedewerkerNaam: string | null | undefined
}

export interface HernoemWerkbonNaamgevingResult {
  /** Nieuwe omschrijving; `undefined` = niet wijzigen. */
  omschrijving?: string
  /** Nieuwe referentie; `undefined` = niet wijzigen. */
  referentie?: string
}

const WOORD_TEKEN = /[\p{L}\p{N}]/u

/** True als `tekst[index..index+lengte)` op woordgrenzen ligt (los woord). */
function isLosWoord(tekst: string, index: number, lengte: number): boolean {
  const voor = index > 0 ? tekst[index - 1]! : ''
  const na = index + lengte < tekst.length ? tekst[index + lengte]! : ''
  if (voor && WOORD_TEKEN.test(voor)) return false
  if (na && WOORD_TEKEN.test(na)) return false
  return true
}

/** Alle startposities waar `woord` als los woord (case-sensitive) in `tekst` staat. */
function losWoordPosities(tekst: string, woord: string): number[] {
  if (!woord) return []
  const posities: number[] = []
  let idx = tekst.indexOf(woord)
  while (idx !== -1) {
    if (isLosWoord(tekst, idx, woord.length)) posities.push(idx)
    idx = tekst.indexOf(woord, idx + 1)
  }
  return posities
}

function binnenSpans(idx: number, len: number, spans: Array<[number, number]>): boolean {
  return spans.some(([start, eind]) => idx < eind && idx + len > start)
}

/**
 * Hernoemt de werkbon-omschrijving en/of -referentie nadat de werkbon naar een
 * andere medewerker is omgepland. Per veld, onafhankelijk van elkaar:
 *
 *   1. Veld is exact de auto-gegenereerde waarde met de oude naam (zowel de
 *      voornaam-variant als de volledige-naam-variant) → hergenereer met de
 *      nieuwe naam via `genereerWerkbonOmschrijving`.
 *   2. Veld bevat de volledige oude naam of de oude voornaam als los woord
 *      (word-boundary, case-sensitive) → vervang alléén dat naamdeel. Eerst
 *      wordt het segment ná het laatste ` - ` geprobeerd; daarna een los
 *      voorkomen elders in de tekst, waarbij voorkomens bínnen de klantnaam
 *      (bv. klant "Kevin's Bouw") niet meetellen.
 *   3. Anders (handmatige tekst zonder de oude naam) → ongemoeid (`undefined`).
 *
 * Een handmatig aangepaste omschrijving wordt zo nooit stilletjes
 * overschreven; alleen het naamdeel beweegt mee.
 */
export function hernoemWerkbonNaamgeving(
  input: HernoemWerkbonNaamgevingInput,
): HernoemWerkbonNaamgevingResult {
  const oudeNaam = input.oudeMedewerkerNaam?.trim() ?? ''
  const nieuweNaam = input.nieuweMedewerkerNaam?.trim() ?? ''
  if (!oudeNaam || !nieuweNaam || oudeNaam === nieuweNaam) return {}

  const oudeVoornaam = oudeNaam.split(/\s+/)[0] ?? ''
  const nieuweVoornaam = nieuweNaam.split(/\s+/)[0] ?? ''
  const klantTrim = input.klantNaam?.trim() ?? ''

  const basis = {
    klantNaam: input.klantNaam,
    dienstType: input.dienstType,
    jaar: input.jaar,
    startWeek: input.startWeek,
  }
  const autoOud = genereerWerkbonOmschrijving({ ...basis, medewerkerNaam: oudeNaam })
  const autoNieuw = genereerWerkbonOmschrijving({ ...basis, medewerkerNaam: nieuweNaam })
  // Sommige paden schrijven de volledige naam i.p.v. alleen de voornaam in het
  // naam-segment — accepteer beide varianten als "exact auto".
  const autoOudVolledig =
    oudeNaam !== oudeVoornaam && autoOud.endsWith(oudeVoornaam)
      ? autoOud.slice(0, autoOud.length - oudeVoornaam.length) + oudeNaam
      : autoOud

  const hernoemVeld = (veld: string | null | undefined): string | undefined => {
    if (!veld) return undefined

    // Stap 1 — exact auto met de oude naam.
    if (veld === autoOud || veld === autoOudVolledig) {
      return autoNieuw && autoNieuw !== veld ? autoNieuw : undefined
    }

    // Stap 2a — naam-segment ná het laatste " - ". Zo blijft een klant-/
    // dienstnaam die de voornaam bevat buiten schot.
    const segmenten = veld.split(' - ')
    if (segmenten.length > 1) {
      const laatste = segmenten[segmenten.length - 1]!.trim()
      if (laatste === oudeNaam || laatste === oudeVoornaam) {
        const vervanging = laatste === oudeNaam ? nieuweNaam : nieuweVoornaam
        if (vervanging === laatste) return undefined
        return [...segmenten.slice(0, -1), vervanging].join(' - ')
      }
    }

    // Stap 2b — los voorkomen elders in de tekst; matches binnen de klantnaam
    // tellen niet mee (klant "Kevin's Bouw" blijft intact).
    const klantSpans: Array<[number, number]> = []
    if (klantTrim) {
      let k = veld.indexOf(klantTrim)
      while (k !== -1) {
        klantSpans.push([k, k + klantTrim.length])
        k = veld.indexOf(klantTrim, k + 1)
      }
    }
    const kandidaten: Array<{ zoek: string; vervang: string }> =
      oudeNaam !== oudeVoornaam
        ? [
            { zoek: oudeNaam, vervang: nieuweNaam },
            { zoek: oudeVoornaam, vervang: nieuweVoornaam },
          ]
        : [{ zoek: oudeNaam, vervang: nieuweVoornaam }]
    for (const { zoek, vervang } of kandidaten) {
      const posities = losWoordPosities(veld, zoek).filter(
        (p) => !binnenSpans(p, zoek.length, klantSpans),
      )
      if (posities.length === 0) continue
      // Vervang het laatste losse voorkomen — het naamdeel staat conventioneel achteraan.
      const p = posities[posities.length - 1]!
      const nieuw = veld.slice(0, p) + vervang + veld.slice(p + zoek.length)
      return nieuw !== veld ? nieuw : undefined
    }

    // Stap 3 — handmatige tekst zonder de oude naam.
    return undefined
  }

  const resultaat: HernoemWerkbonNaamgevingResult = {}
  const omschrijving = hernoemVeld(input.huidigeOmschrijving)
  if (omschrijving !== undefined) resultaat.omschrijving = omschrijving
  const referentie = hernoemVeld(input.huidigeReferentie)
  if (referentie !== undefined) resultaat.referentie = referentie
  return resultaat
}

// ── Overuurtarief default (1.5×) ────────────────────────────────────────────

/**
 * Default overuurtarief: basistarief × 1.5, afgerond op 2 decimalen.
 * Wordt door de UI als read-only preview getoond zolang er geen
 * klant-override (`ClientTariff.overtimeRate`) is.
 */
export function defaultOveruurtarief(uurtarief: number): number {
  if (!Number.isFinite(uurtarief) || uurtarief <= 0) return 0
  return Math.round(uurtarief * 1.5 * 100) / 100
}

// ── Type-default uurtarief (fs-pro fallback) ────────────────────────────────

export const FS_PRO_TYPE_TARIEVEN: Record<WerkbonType, number> = {
  ONDERHOUD: 65.0,
  SERVICE: 52.5,
  PROJECT: 75.0,
  // Kantoor interne bon (S3): interne werkbon zonder tarief — niet factureerbaar.
  KANTOOR: 0,
}
